import { wrapRequest } from "./nav";
import { fetchGraphQL } from "@/services/graphqlEndPointCommonSetting/fetchGraphQL";
import { wrapGqlRequest } from "../services/graphqlEndPointCommonSetting/wrapGqlRequest";
import {
  PRODUCT_DETAILS_QUERY,
  PRODUCTS_LIST_QUERY,
  PRODUCT_SEARCH_QUERY,
  MULTIPLE_PRODUCTS_QUERY,
  ATTRIBUTES_LIST_QUERY,
  PRODUCT_VARIANTS_QUERY,
  CUSTOMER_ORDER_QUERY,
  GUEST_ORDER_QUERY,
} from "./graphQl/product.queries";

const vueAppMagenotApiUrl = import.meta.env.VITE_MAGENTO_API_URL;

const vueAppCraneOrderUrl = import.meta.env.VITE_CRANE_ORDER_URL;
const imageLibApiUrl = import.meta.env.VITE_IMAGELIB_API_URL;


// GraphQL transformation utility
const transformProductForLegacyAPI = (gqlProduct) => {

  if (!gqlProduct) return null;

  const transformed = {
    id: gqlProduct.id,
    name: gqlProduct.name,
    sku: gqlProduct.sku,
    inStock: gqlProduct.inStock,
    status: gqlProduct?.status ?? 1,
    typeId: gqlProduct.typeId || "simple",
    description: gqlProduct.description || "",
    shortDescription: gqlProduct.shortDescription || "",
    metaTitle: gqlProduct.metaTitle || "",
    metaDescription: gqlProduct.metaDescription || "",
    metaKeyword: gqlProduct.metaKeyword || "",
    urlKey: gqlProduct.urlKey || "",
    visibility: gqlProduct?.visibility,
    price: 0,
    options: gqlProduct?.inputOptions?.map(({ id, ...rest }) => ({
      optionId: id,
      ...rest,
    })),
    unitDescription:
      gqlProduct.attributes?.find((attr) => attr.name === "unit_description")
        ?.value || "",
    builderVersion:
      gqlProduct.attributes?.find((attr) => attr.name === "builder_version")
        ?.value || "",
    isPersonalize:
      gqlProduct.attributes?.find((attr) => attr.name === "personalized")
        ?.value == "Yes"
        ? "1"
        : "0",
    typename: gqlProduct.__typename,
    mediaGalleryEntries:
      gqlProduct.images?.map((img, index) => ({
        id: index + 1,
        mediaType: "image",
        label: img.label || "",
        file: img.url,
        position: index + 1,
        disabled: false,
        types: img.roles || ["image"],
      })) || [],

    // Create attributes array once to avoid duplication
    customAttribute:
      gqlProduct.attributes?.map((attr) => {
        /*if (attr.name === "personalized" && attr.value === "Yes") {
          return {
            attributeCode: "is_personalize",
            value: "1",
            label: "Is Personalize",
          };
        }*/
        return {
          attributeCode: attr.name,
          value: attr.value,
          label: attr.label,
        };
      }) || [],

    relatedLinksProduct:
      gqlProduct.links
        ?.filter((link) => link.product)
        ?.sort((a, b) => a.position - b.position)   // ⭐ position sorting
        ?.map((link) => ({
          name: link.product.name,
          sku: link.product.sku,
          image:
            link.product.images?.map((img) => ({
              file: img.url,
              label: img.label,
            })) || [],
          customAttribute:
            link?.product?.attributes?.map((attr) => {
              if (attr.name === "personalized" && attr.value === "Yes") {
                return {
                  attributeCode: "is_personalize",
                  value: "1",
                  label: "Is Personalize",
                };
              }
              return {
                attributeCode: attr.name,
                value: attr.value,
                label: attr.label,
              };
            }) || [],
        })) || [],

    configurableProductLinks:
      gqlProduct.variants?.map((variant) => ({
        id: variant?.product?.id ?? variant?.id,
        sku: variant?.product?.sku ?? variant?.sku,
        name: variant?.product?.name ?? variant?.name,
        price: variant?.price || 0,
        attributes: variant?.attributes || variant?.customAttributes || [],
        mediaGalleryEntries:
          (
            variant?.product?.media_gallery ?? variant?.mediaGalleryEntries
          )?.map((img, index) => ({
            id: index + 1,
            mediaType: "image",
            label: img.label || "",
            file: img?.url ?? img?.file,
            position: index + 1,
            disabled: false,
            types: ["image"],
          })) || [],
        customAttributes:
          (variant?.attributes ?? variant?.customAttributes)?.map((attr) => ({
            attributeCode: attr?.code ?? attr?.attributeCode,
            value: attr?.value_index ?? attr?.value,
            label: attr?.label,
          })) || [],
      })) || [],

    extensionAttributes: {
      categoryLinks: [],
      configurableProductLinks:
        gqlProduct.variants?.map(
          (variant) => variant?.product?.id ?? variant.id,
        ) || [],
      categoryLinksPrimary: [],
    },
    configurableProductLinksDetails: gqlProduct.variants
      ?.filter((product) => product.inStock) // 👈 only in-stock products
      .map((product) => {
        const letterAttr = product.customAttributes.find(
          (attr) => attr.attributeCode === "letter",
        );

        return {
          entity_id: product.id,
          sku: product.sku,
          letterId: product.selections?.[0] || null,
          letterLabel: letterAttr ? letterAttr.value : null,
        };
      }),
  };


  // Extract price from GraphQL first
  let productPrice = 0;
  if (gqlProduct.price) {
    if (gqlProduct.price.final && gqlProduct.price.final.amount) {
      productPrice = parseFloat(gqlProduct.price.final.amount.value) || 0;
      transformed.price = productPrice;
    } else if (gqlProduct.price.regular && gqlProduct.price.regular.amount) {
      productPrice = parseFloat(gqlProduct.price.regular.amount.value) || 0;
      transformed.price = productPrice;
    }
    // regular price
    const regularPrice = gqlProduct.price?.regular?.amount?.value
      ? parseFloat(gqlProduct.price.regular.amount.value)
      : 0;

    // final price
    const finalPrice = gqlProduct.price?.final?.amount?.value
      ? parseFloat(gqlProduct.price.final.amount.value)
      : regularPrice;

    // finalPrice always regular
    transformed.finalPrice = regularPrice;

    // specialPrice ONLY when prices are different
    if (regularPrice !== finalPrice) {
      transformed.specialPrice = finalPrice;
    } else {
      transformed.specialPrice = 0; // or delete / undefined if you want
    }
  } else if (gqlProduct.priceRange) {
    if (
      gqlProduct.priceRange.minimum &&
      gqlProduct.priceRange.minimum.final &&
      gqlProduct.priceRange.minimum.final.amount
    ) {
      productPrice =
        parseFloat(gqlProduct.priceRange.minimum.final.amount.value) || 0;
      transformed.price = productPrice;
    }
  }


  // Add stock information from GraphQL
  transformed.inStock = gqlProduct.inStock;

  // Alias customAttributes to customAttribute for backward compatibility
  transformed.customAttributes = transformed.customAttribute;

  return transformed;
};

export const doFetchOrderProductsList = wrapRequest((emailId) => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
  };
  return fetch(
    `${vueAppMagenotApiUrl}/rest/V1/crorders?searchCriteria[filter_groups][0][filters][0][field]=customer_email&searchCriteria[filter_groups][0][filters][0][value]=${emailId}`,
    requestOptions,
  );
});

export const doFetchCustomerOrderProduct = async (orderId) => {
  const variables = {
    currentPage: 1,
    pageSize: 1,
    filter: {
      number: {
        eq: orderId,
      },
    },
  };

  const response = await wrapGqlRequest(CUSTOMER_ORDER_QUERY, variables);
  const orderData = response?.customer?.orders?.items?.[0] || {};
  const customerData = response?.customer || {};
  const finalResponse = {
    email: customerData.email,
    firstname: customerData.firstname,
    lastname: customerData.lastname,
    middlename: customerData.middlename,
    entityId: orderData.id,
    incrementId: orderData.number,
    status: orderData.status,
    createdAt: orderData.order_date,
    shippingDescription: orderData.shipping_method,
    shipping_carrier: orderData.carrier,
    billingAddress: orderData.billing_address,
    shippingAddress: orderData.shipping_address,

    items: orderData.items.map((item) => ({
      item_id: item.id,
      name: item.product_name,
      sku: item?.product?.sku,
      qtyOrdered: item.quantity_ordered,
      basePrice: item.product_sale_price?.value,
      basePriceInclTax: item.prices?.price_including_tax?.value,
      rowTotal: item.prices?.row_total?.value,
      baseRowTotalInclTax: item.prices?.row_total_including_tax?.value,
      baseCurrencyCode: item.product_sale_price?.currency,
      brandName: item.product?.custom_attributesV2?.items.find(
        (i) => i.code === "brand",
      )?.selected_options?.[0]?.label,
    })),
    payment: {
      method: orderData.payment_methods?.[0]?.name || "",
      additionalInformation: [
        orderData.payment_methods?.[0]?.name || "No Payment Information",
        orderData.payment_methods?.[0]?.type || "free",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
    },
    baseGrandTotal: orderData.total?.base_grand_total?.value,
    grandTotal: orderData.total?.grand_total?.value,
    grandTotalExclTax: orderData.total?.grand_total_excl_tax?.value,
    grandTotalInclTax: orderData.total?.grand_total_incl_tax?.value,
    baseSubtotalExclTax: orderData.total?.subtotal_excl_tax?.value,
    baseSubtotalInclTax: orderData.total?.subtotal_incl_tax?.value,
    base_currency_code: orderData.total?.grand_total?.currency,
    baseTaxAmount: orderData.total?.total_tax?.value,
    baseTaxCurrency: orderData.total?.total_tax?.currency,
    baseShippingExclTax: orderData.total?.total_shipping?.value,
    baseShippingInclTax: orderData.total?.total_shipping?.value,
    baseShippingCurrency: orderData.total?.total_shipping?.currency,
    discountAmount: orderData.total?.discounts[0]?.amount?.value,
    discountCurrency: orderData.total?.discounts[0]?.amount?.currency,
    couponCode: orderData.total?.discounts[0]?.amount?.label,
  };
  return finalResponse;
};

export const doFetchProductInvoice = async () => {
  let Invoice = {
    base_currency_code: "string",
    base_discount_amount: 0,
    base_grand_total: 0,
    base_discount_tax_compensation_amount: 0,
    base_shipping_amount: 0,
    base_shipping_discount_tax_compensation_amnt: 0,
    base_shipping_incl_tax: 0,
    base_shipping_tax_amount: 0,
    base_subtotal: 0,
    base_subtotal_incl_tax: 0,
    base_tax_amount: 0,
    base_total_refunded: 0,
    base_to_global_rate: 0,
    base_to_order_rate: 0,
    billing_address_id: 0,
    can_void_flag: 0,
    created_at: "string",
    discount_amount: 0,
    discount_description: "string",
    email_sent: 0,
    entity_id: 0,
    global_currency_code: "string",
    grand_total: 32,
    discount_tax_compensation_amount: 0,
    increment_id: "string",
    is_used_for_refund: 0,
    order_currency_code: "string",
    order_id: 0,
    shipping_address_id: 0,
    shipping_amount: 0,
    shipping_discount_tax_compensation_amount: 0,
    shipping_incl_tax: 0,
    shipping_tax_amount: 0,
    state: 0,
    store_currency_code: "string",
    store_id: 0,
    store_to_base_rate: 0,
    store_to_order_rate: 0,
    subtotal: 32,
    subtotal_incl_tax: 0,
    tax_amount: 0,
    total_qty: 1,
    transaction_id: "string",
    updated_at: "string",
    items: [
      {
        additional_data: "string",
        base_cost: 0,
        base_discount_amount: 0,
        base_discount_tax_compensation_amount: 0,
        base_price: 0,
        base_price_incl_tax: 0,
        base_row_total: 0,
        base_row_total_incl_tax: 0,
        base_tax_amount: 0,
        description: "string",
        discount_amount: 0,
        entity_id: 0,
        discount_tax_compensation_amount: 0,
        name: "ENGRAVED SUN CARD",
        parent_id: 0,
        price: 32,
        price_incl_tax: 0,
        product_id: 0,
        row_total: 32,
        row_total_incl_tax: 0,
        sku: "CC3700",
        tax_amount: 0,
        extension_attributes: {},
        order_item_id: 0,
        qty: 1,
      },
    ],
    comments: [
      {
        is_customer_notified: 0,
        parent_id: 0,
        extension_attributes: {},
        comment: "string",
        is_visible_on_front: 0,
        created_at: "string",
        entity_id: 0,
      },
    ],
    extension_attributes: {
      base_customer_balance_amount: 0,
      customer_balance_amount: 0,
      base_gift_cards_amount: 0,
      gift_cards_amount: 0,
      gw_base_price: "string",
      gw_price: "string",
      gw_items_base_price: "string",
      gw_items_price: "string",
      gw_card_base_price: "string",
      gw_card_price: "string",
      gw_base_tax_amount: "string",
      gw_tax_amount: "string",
      gw_items_base_tax_amount: "string",
      gw_items_tax_amount: "string",
      gw_card_base_tax_amount: "string",
      gw_card_tax_amount: "string",
    },
  };
  return Invoice;
};

export const doFetchProductsList = async (
  pageSize = 27,
  currentPage = 1,
  phrase = "",
) => {
  try {
    const variables = {
      phrase: phrase || "",
    };
    const response = await fetchGraphQL(PRODUCTS_LIST_QUERY, variables);
    const transformedResponse = {
      items:
        response?.productSearch?.items
          ?.map((item) =>
            item.productView
              ? transformProductForLegacyAPI(item.productView)
              : null,
          )
          .filter(Boolean) || [],
      total_count: response?.productSearch?.total_count || 0,
    };

    return { json: () => Promise.resolve(transformedResponse) };
  } catch (error) {
    console.error("❌ GraphQL Product List Error:", error);
    throw error;
  }
};

export const doFetchAttributeList = async () => {
  try {
    const response = await fetchGraphQL(ATTRIBUTES_LIST_QUERY);

    if (response?.attributesList?.items?.length > 0) {
      const transformedAttributes = response.attributesList.items.map(
        (attr) => ({
          attribute_id: attr.code,
          attributeCode: attr.code,
          frontend_input: attr.frontend_input || "text",
          is_required: attr.is_required || false,
          default_frontend_label: attr.label,
          frontendLabels: [{ store_id: 0, label: attr.label }],
          options: attr.options || [],
        }),
      );

      return { json: () => Promise.resolve({ items: transformedAttributes }) };
    }

    return { json: () => Promise.resolve({ items: [] }) };
  } catch (error) {
    console.error("❌ GraphQL Attribute List Error:", error);
    return { json: () => Promise.resolve({ items: [] }) };
  }
};

export const doFetchFilterOptions = async () => {
  let filterList = {
    items: [
      {
        attributeCode: "category",
        options: [
          {
            label: "Black",
            value: "49",
          },
          {
            label: "Blue",
            value: "50",
          },
          {
            label: "Brown",
            value: "51",
          },
          {
            label: "Gray",
            value: "52",
          },
        ],
      },
      {
        attributeCode: "personalized",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona&reg; performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "brand",
        options: [
          {
            label: "Normal",
            value: "3",
          },
          {
            label: "Cocona&reg; performance fabric",
            value: "6",
          },
          {
            label: "Canvas",
            value: "8",
          },
          {
            label: "Wool",
            value: "8",
          },
        ],
      },
      {
        attributeCode: "paper weight",
        options: [
          {
            label: "Normal",
            value: "3",
          },
          {
            label: "Cocona",
            value: "6",
          },
          {
            label: "Canvas",
            value: "8",
          },
          {
            label: "Wool",
            value: "8",
          },
        ],
      },
      {
        attributeCode: "product type/form",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "print method",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "paper color",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona&reg; performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "design style",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona&reg; performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "design color",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "theme",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "photo card",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
      {
        attributeCode: "price",
        options: [
          {
            label: "Burlap",
            value: "31",
          },
          {
            label: "Cocona performance fabric",
            value: "143",
          },
          {
            label: "Canvas",
            value: "32",
          },
          {
            label: "Wool",
            value: "144",
          },
        ],
      },
    ],
  };
  return filterList;
};
export const doFetchProductDetails = async (sku) => {
  try {
    if (!sku || sku === "") {
      throw new Error(
        `SKU is required but not provided. Received: ${JSON.stringify(sku)}`,
      );
    }

    const variables = { sku: String(sku) };
    const response = await fetchGraphQL(PRODUCT_DETAILS_QUERY, variables);

    if (response?.products?.length > 0) {
      if (response?.products[0]?.__typename === "ComplexProductView") {
        console.log("Fetching variants for configurable product:", sku);
        response.products[0].variants =
          (await (await doFetchProductVariants(response?.products[0]?.sku))
            .json()
            .then((res) => res.items)) || [];
      }
      const product = transformProductForLegacyAPI(response.products[0]);
      console.log("variant_product", product);
      return { json: () => Promise.resolve([product]) };
    }

    throw new Error(`Product with SKU ${sku} not found`);
  } catch (error) {
    console.error("❌ GraphQL Product Details Error:", error);
    throw error;
  }
};

export const doFetchProductVariants = async (sku) => {
  try {
    if (!sku || sku === "") {
      return { json: () => Promise.resolve({ items: [] }) };
    }

    const variables = { sku: String(sku), pageSize: 100 };
    const response = await fetchGraphQL(PRODUCT_VARIANTS_QUERY, variables);
    console.log("Variants Response:", response);
    if (response?.variants?.variants?.length > 0) {
      const transformedVariants = response.variants.variants.map((variant) => {
        const transformedVariant = transformProductForLegacyAPI(
          variant.product,
        );
        // Add selection information from the variant
        transformedVariant.selections = variant.selections || [];
        return transformedVariant;
      });

      return { json: () => Promise.resolve({ items: transformedVariants }) };
    }

    return { json: () => Promise.resolve({ items: [] }) };
  } catch (error) {
    console.error("❌ GraphQL Product Variants Error:", error);
    return { json: () => Promise.resolve({ items: [] }) };
  }
};

export const doFetchAttributeFilterProducts = wrapRequest(() => { });

export const doFetchSearchProducts = async (
  searchTerm,
  pageSize = 27,
  currentPage = 1,
  filter = [],
) => {
  try {
    const variables = {
      phrase: searchTerm,
      filter,
    };

    const response = await fetchGraphQL(PRODUCT_SEARCH_QUERY, variables);
    const transformedResponse = {
      items:
        response?.productSearch?.items
          ?.map((item) =>
            item.productView
              ? transformProductForLegacyAPI(item.productView)
              : null,
          )
          .filter(Boolean) || [],
      total_count: response?.productSearch?.total_count || 0,
    };

    return { json: () => Promise.resolve(transformedResponse) };
  } catch (error) {
    console.error("❌ GraphQL Product Search Error:", error);
    throw error;
  }
};
export const doFetchSortProducts = wrapRequest(
  (pageNo, pageSize, sortBy, categoryIds, productNames, sideFilter) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-type": "application/json",
      },
    };
    let sortFlag = "ASC";
    if (sortBy === "relevance") {
      sortFlag = "DESC";
    }
    let extraAttribute = "";
    if (pageSize != "ALL") {
      extraAttribute = `searchCriteria[pageSize]=${pageSize}&searchCriteria[currentPage]=${pageNo}&`;
    }
    if (categoryIds) {
      let query = "";
      query = `?searchCriteria[filter_groups][0][filters][0][field]=entity_id&searchCriteria[filter_groups][0][filters][0][value]=${categoryIds}&searchCriteria[filter_groups][0][filters][0][condition_type]=in&searchCriteria[sortOrders][0][field]=${sortBy}&searchCriteria[sortOrders][0][direction]=${sortFlag}`;
      console.log(
        "product",
        `${vueAppMagenotApiUrl}/rest/V1/crproducts/${query}`,
      );
      return fetch(
        `${vueAppMagenotApiUrl}/rest/V1/crproducts/${query}`,
        requestOptions,
      );
    } else {
      let nameSearchQuery = "";
      let filterQuery = "";
      let increment = 0;
      let query = "";
      if (productNames) {
        productNames.split(" ").forEach((name, index) => {
          nameSearchQuery += `searchCriteria[filter_groups][0][filters][${index}][field]=name&searchCriteria[filter_groups][0][filters][${index}][value]=%25${name}%25&searchCriteria[filter_groups][0][filters][${index}][condition_type]=like&`;
          increment++;
        });
      }

      if (sideFilter != "e30=") {
        let filtertoNormal = JSON.parse(atob(sideFilter));
        Object.getOwnPropertyNames(filtertoNormal).forEach((key) => {
          filterQuery += `searchCriteria[filter_groups][${increment}][filters][0][field]=${key}&searchCriteria[filter_groups][${increment}][filters][0][value]=${filtertoNormal[key]}&`;
          increment++;
        });
      }

      query = `?${extraAttribute}${nameSearchQuery}${filterQuery}searchCriteria[filter_groups][${increment}][filters][0][field]=status&searchCriteria[filter_groups][${increment}][filters][0][value]=1&searchCriteria[filter_groups][${increment}][filters][0][condition_type]=eq&searchCriteria[sortOrders][0][field]=${sortBy}&searchCriteria[sortOrders][0][direction]=DESC`;

      console.log(
        "productNames",
        `${vueAppMagenotApiUrl}/rest/V1/crproducts/${query}`,
      );
      return fetch(
        `${vueAppMagenotApiUrl}/rest/V1/crproducts/${query}`,
        requestOptions,
      );
    }
  },
);
export const doFetchCategoryList = wrapRequest(() => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
    },
  };
  return fetch(
    `${vueAppMagenotApiUrl}/rest/all/V1/crcategories`,
    requestOptions,
  );
});

export const dofetchCategoryListIdWise = wrapRequest(() => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
    },
  };
  return fetch(
    `${vueAppMagenotApiUrl}/rest/V1/crcategories/list?searchCriteria&fields=items[id,parent_id,name,custom_attributes]`,
    requestOptions,
  );
});
export const doFetchCatelogProducts = async (searchCriteria) => {
  try {
    const variables = {
      phrase: searchCriteria,
    };

    const response = await fetchGraphQL(PRODUCT_SEARCH_QUERY, variables);
    const transformedResponse = {
      items:
        response?.productSearch?.items
          ?.map((item) =>
            item.productView
              ? transformProductForLegacyAPI(item.productView)
              : null,
          )
          .filter(Boolean) || [],
      total_count: response?.productSearch?.total_count || 0,
    };

    return { json: () => Promise.resolve(transformedResponse) };
  } catch (error) {
    console.error("❌ GraphQL Catalog Products Error:", error);
    throw error;
  }
};

export const doFetchRecentlyViewedList = async (productIds) => {
  try {
    if (!productIds || productIds.length === 0) {
      return { json: () => Promise.resolve([]) };
    }

    const variables = {
      ids: productIds.map((id) => String(id)),
    };

    const response = await fetchGraphQL(MULTIPLE_PRODUCTS_QUERY, variables);

    if (response?.products?.length > 0) {
      const transformedProducts = response.products.map((gqlProduct) =>
        transformProductForLegacyAPI(gqlProduct),
      );

      return { json: () => Promise.resolve(transformedProducts) };
    }

    return { json: () => Promise.resolve([]) };
  } catch (error) {
    console.error("❌ GraphQL Multiple Products Error:", error);
    return { json: () => Promise.resolve([]) };
  }
};
export const doSideBarFilter = wrapRequest(
  (pageNo, pageSize, sortBy, categoryId, sideFilter, search) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-type": "application/json",
      },
    };

    let extraAttribute = "";
    if (pageSize != "ALL") {
      extraAttribute = `searchCriteria[pageSize]=${pageSize}&searchCriteria[currentPage]=${pageNo - 1
        }&`;
    }

    let sortFlag = "ASC";
    if (sortBy === "relevance") {
      sortFlag = "DESC";
    }

    if (categoryId) {
      let nameSearchQuery = "";
      if (sideFilter != "e30=") {
        let filtertoNormal = JSON.parse(atob(sideFilter));
        Object.getOwnPropertyNames(filtertoNormal).forEach((key, index) => {
          nameSearchQuery += `searchCriteria[filter_groups][${index + 2
            }][filters][0][field]=${key}&searchCriteria[filter_groups][${index + 2
            }][filters][0][value]=${filtertoNormal[key]}&`;
        });
      }

      console.log(
        "searchCat",
        `${vueAppMagenotApiUrl}/rest/V1/search?searchCriteria[filter_groups][0][filters][0][field]=category_ids&searchCriteria[filter_groups][0][filters][0][value]=${categoryId}&searchCriteria[filter_groups][1][filters][0][field]=visibility&searchCriteria[filter_groups][1][filters][0][value]=4&${nameSearchQuery}${extraAttribute}searchCriteria[sortOrders][0][field]=${sortBy}&searchCriteria[sortOrders][0][direction]=${sortFlag}&searchCriteria[requestName]=catalog_view_container`,
      );

      return fetch(
        `${vueAppMagenotApiUrl}/rest/V1/search?searchCriteria[filter_groups][0][filters][0][field]=category_ids&searchCriteria[filter_groups][0][filters][0][value]=${categoryId}&searchCriteria[filter_groups][1][filters][0][field]=visibility&searchCriteria[filter_groups][1][filters][0][value]=4&${nameSearchQuery}${extraAttribute}searchCriteria[sortOrders][0][field]=${sortBy}&searchCriteria[sortOrders][0][direction]=${sortFlag}&searchCriteria[requestName]=catalog_view_container`,
        requestOptions,
      );
    } else if (search) {
      let nameSearchQuery = "";
      let filterQuery = "";
      let increment = 0;

      if (search) {
        nameSearchQuery += `searchCriteria[filter_groups][0][filters][0][field]=search_term&searchCriteria[filter_groups][0][filters][0][value]=${search}&`;
        increment++;
      }
      if (sideFilter != "e30=") {
        let filtertoNormal = JSON.parse(atob(sideFilter));
        Object.getOwnPropertyNames(filtertoNormal).forEach((key) => {
          filterQuery += `searchCriteria[filter_groups][${increment}][filters][0][field]=${key}&searchCriteria[filter_groups][${increment}][filters][0][value]=${filtertoNormal[key]}&`;
          increment++;
        });
      }
      console.log(
        "search",
        `${vueAppMagenotApiUrl}/rest/V1/search?${nameSearchQuery}${filterQuery}searchCriteria[filter_groups][${increment}][filters][0][field]=visibility&searchCriteria[filter_groups][${increment}][filters][0][value]=4&${extraAttribute}searchCriteria[sortOrders][0][field]=${sortBy}&searchCriteria[sortOrders][0][direction]=${sortFlag}&searchCriteria[requestName]=quick_search_container`,
      );
      return fetch(
        `${vueAppMagenotApiUrl}/rest/V1/search?${nameSearchQuery}${filterQuery}searchCriteria[filter_groups][${increment}][filters][0][field]=visibility&searchCriteria[filter_groups][${increment}][filters][0][value]=4&${extraAttribute}searchCriteria[sortOrders][0][field]=${sortBy}&searchCriteria[sortOrders][0][direction]=${sortFlag}&searchCriteria[requestName]=quick_search_container`,
        requestOptions,
      );
    }
  },
);

export const doFetchPartPrice = wrapRequest((part) => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
    body: JSON.stringify(part),
  };
  return fetch(`${vueAppCraneOrderUrl}/pricer/price`, requestOptions);
});

export const doPlaceCraneOrder = wrapRequest((order) => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(order),
  };
  return fetch(`${vueAppCraneOrderUrl}/Incoming/input`, requestOptions);
});
export const doPatchOne = wrapRequest((cms) => {
  let date = new Date().toISOString();
  cms.requested_at = date;
  const requestOptions = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/vnd.pgrst.object+json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(cms),
  };
  return fetch(
    `${imageLibApiUrl}/magento_sync?element=eq.${cms.element}`,
    requestOptions,
  );
});

export const doFetchProductTags = wrapRequest((sku) => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Accept: "application/vnd.pgrst.object+json",
    },
  };
  return fetch(
    `${imageLibApiUrl}/product?sku=eq.${sku}&select=tags,sku`,
    requestOptions,
  );
});

export const doFetchProductsAttributes = wrapRequest((sku) => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: "Bearer 58rcjzlop08412gs0xa42q3qna21zxo2",
      "Access-Control-Allow-Origin": "*",
    },
  };
  return fetch(
    `${vueAppMagenotApiUrl}/rest/all/V1/crproducts/${sku}?fields=sku,custom_attributes`,
    requestOptions,
  );
});
export const doFetchConfiguredProductDetails = wrapRequest((productId) => {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
  };
  return fetch(
    `${vueAppMagenotApiUrl}/rest/V1/crproducts?searchCriteria[filter_groups][0][filters][0][field]=entity_id&searchCriteria[filter_groups][0][filters][0][value]=${productId}?searchCriteria&fields=items[price,sku,media_gallery_entries,custom_attributes]`,
    requestOptions,
  );
});

export const doFetchOrderProduct = async (orderId) => {
  try {
    const variables = {
      currentPage: 1,
      pageSize: 1,
      filter: {
        number: {
          eq: orderId,
        },
      },
    };

    const response = await wrapGqlRequest(CUSTOMER_ORDER_QUERY, variables);
    const orderData = response?.customer?.orders?.items?.[0] || {};
    const customerData = response?.customer || {};
    const finalResponse = {
      email: customerData.email,
      firstname: customerData.firstname,
      lastname: customerData.lastname,
      middlename: customerData.middlename,
      entityId: orderData.id,
      incrementId: orderData.number,

      status: orderData.status,
      createdAt: orderData.order_date,
      shippingDescription: orderData.shipping_method,
      shipping_carrier: orderData.carrier,
      billingAddress: orderData.billing_address,
      shippingAddress: orderData.shipping_address,
      items: orderData.items.map((item) => ({
        item_id: item.id,
        name: item.product_name,
        sku: item?.product?.sku,
        qtyOrdered: item.quantity_ordered,
        basePrice: item.product_sale_price?.value,
        basePriceInclTax: item.prices?.price_including_tax?.value,
        rowTotal: item.prices?.row_total?.value,
        baseRowTotalInclTax: item.prices?.row_total_including_tax?.value,
        baseCurrencyCode: item.product_sale_price?.currency,
        selectedOption: item?.selected_options,
        brandName: item.product?.custom_attributesV2?.items.find(
          (i) => i.code === "brand",
        )?.selected_options?.[0]?.label,
      })),
      payment: {
        method: orderData.payment_methods?.[0]?.name || "",
        additionalInformation: [
          orderData.payment_methods?.[0]?.name || "No Payment Information",
          orderData.payment_methods?.[0]?.type || "free",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ],
      },
      baseGrandTotal: orderData.total?.base_grand_total?.value,
      grandTotal: orderData.total?.grand_total?.value,
      grandTotalExclTax: orderData.total?.grand_total_excl_tax?.value,
      grandTotalInclTax: orderData.total?.grand_total_incl_tax?.value,
      baseSubtotalExclTax: orderData.total?.subtotal_excl_tax?.value,
      baseSubtotalInclTax: orderData.total?.subtotal_incl_tax?.value,
      base_currency_code: orderData.total?.grand_total?.currency,

      baseTaxAmount: orderData.total?.total_tax?.value,
      baseTaxCurrency: orderData.total?.total_tax?.currency,
      baseShippingExclTax: orderData.total?.total_shipping?.value,
      baseShippingInclTax: orderData.total?.total_shipping?.value,
      baseShippingCurrency: orderData.total?.total_shipping?.currency,

      discountAmount: orderData.total?.discounts[0]?.amount?.value,
      discountCurrency: orderData.total?.discounts[0]?.amount?.currency,
      couponCode: orderData.total?.discounts[0]?.amount?.label,
    };
    return finalResponse;
  } catch (error) {
    console.error("❌ GraphQL Catalog Products Error:", error);
    throw error;
  }
};
export const doFetchOrderProductForGuest = async ({
  orderId,
  email,
  lastName,
}) => {
  try {
    const variables = {
      input: {
        number: orderId,
        email: email,
        lastname: lastName,
      },
    };

    const response = await wrapGqlRequest(GUEST_ORDER_QUERY, variables);

    const orderData = response?.guestOrder || {};

    const finalResponse = {
      entityId: orderData.id,
      incrementId: orderData.number,
      email: orderData.email,
      customerEmail: orderData.email,
      firstname: orderData.customer_info.firstname,
      lastname: orderData.customer_info.lastname,
      middlename: orderData.customer_info.middlename,
      status: orderData.status,
      createdAt: orderData.order_date,
      shippingDescription: orderData.shipping_method,
      shipping_carrier: orderData.carrier,
      billingAddress: orderData.billing_address,
      shippingAddress: orderData.shipping_address,

      items: orderData.items.map((item) => ({
        item_id: item.id,
        name: item.product_name,
        sku: item?.product?.product_sku,
        qtyOrdered: item.quantity_ordered,
        basePrice: item.product_sale_price?.value,
        basePriceInclTax: item.prices?.price_including_tax?.value,
        rowTotal: item.prices?.row_total?.value,
        baseRowTotalInclTax: item.prices?.row_total_including_tax?.value,
        baseCurrencyCode: item.product_sale_price?.currency,
        brandName: item.product?.custom_attributesV2?.items.find(
          (i) => i.code === "brand",
        )?.selected_options?.[0]?.label,
      })),
      payment: {
        method: orderData.payment_methods?.[0]?.name || "",
        additionalInformation: [
          orderData.payment_methods?.[0]?.name || "No Payment Information",
          orderData.payment_methods?.[0]?.type || "free",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ],
      },
      baseGrandTotal: orderData.total?.base_grand_total?.value,
      grandTotal: orderData.total?.grand_total?.value,
      grandTotalExclTax: orderData.total?.grand_total_excl_tax?.value,
      grandTotalInclTax: orderData.total?.grand_total_incl_tax?.value,
      baseSubtotalExclTax: orderData.total?.subtotal_excl_tax?.value,
      baseSubtotalInclTax: orderData.total?.subtotal_incl_tax?.value,
      base_currency_code: orderData.total?.grand_total?.currency,

      baseTaxAmount: orderData.total?.total_tax?.value,
      baseTaxCurrency: orderData.total?.total_tax?.currency,
      baseShippingExclTax: orderData.total?.total_shipping?.value,
      baseShippingInclTax: orderData.total?.total_shipping?.value,
      baseShippingCurrency: orderData.total?.total_shipping?.currency,

      discountAmount: orderData.total?.discounts[0]?.amount?.value,
      discountCurrency: orderData.total?.discounts[0]?.amount?.currency,
      couponCode: orderData.total?.discounts[0]?.amount?.label,
    };

    return finalResponse;
  } catch (error) {
    console.error("❌ GraphQL Catalog Products Error:", error);
    throw error;
  }
};
